import { useEffect } from 'react';
import { hideSpinnerChrome } from './spinnerMfeBridge';

/*
  Keeps the spinner MFE's own chrome hidden (see hideSpinnerChrome) for as long as the pane
  is mounted. The MFE mounts/re-renders its UI asynchronously and frequently while loading
  and rotating, so a single one-shot pass isn't enough — but reacting to every mutation with
  a full-subtree scan burns the main thread during rotation. Instead a single MutationObserver
  coalesces bursts into ONE rAF-batched pass, so the scan runs at most once per frame no
  matter how many mutations the MFE emits.
*/
export function useHideSpinnerChrome(containerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let frameId = 0;
    const run = () => {
      frameId = 0;
      hideSpinnerChrome(el);
    };
    const schedule = () => {
      if (frameId) return; // a pass is already queued for this frame
      frameId = requestAnimationFrame(run);
    };

    hideSpinnerChrome(el);
    const observer = new MutationObserver(schedule);
    observer.observe(el, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [containerRef]);
}
